import React, { useState } from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ParcelDescriptionFieldProps {
  description: string;
  onDescriptionChange: (description: string) => void;
}

const ParcelDescriptionField: React.FC<ParcelDescriptionFieldProps> = ({
  description,
  onDescriptionChange
}) => {
  const [showDangerousGoodsDialog, setShowDangerousGoodsDialog] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label>Description of Contents (Optional)</Label>
        <Badge 
          variant="outline" 
          className="text-xs text-muted-foreground cursor-pointer hover:text-primary hover:border-primary transition-colors"
          onClick={() => setShowDangerousGoodsDialog(true)}
        >
          <AlertTriangle className="h-3 w-3 mr-1" />
          Dangerous goods? Click here
        </Badge>
      </div>
      <Textarea 
        value={description} 
        onChange={e => onDescriptionChange(e.target.value)} 
        placeholder="Please describe the contents of your parcel" 
        className="min-h-[80px]" 
      />

      {/* Dangerous Goods Information Dialog */}
      <Dialog open={showDangerousGoodsDialog} onOpenChange={setShowDangerousGoodsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-lg">
                <ShieldAlert className="h-5 w-5 text-destructive" />
              </div>
              <DialogTitle className="text-xl font-medium">
                Dangerous Goods Policy
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-muted-foreground">
              Important information about prohibited items
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-foreground leading-relaxed">
                Please ensure that <strong>dangerous goods</strong>, as defined in SWIFT's terms and conditions, 
                are <strong>not included</strong> in your shipment. These items require special handling or 
                packaging and may cause harm to persons or property.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Examples of prohibited items include:</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Flammable liquids and gases</li>
                <li>Explosives and ammunition</li>
                <li>Corrosive substances</li>
                <li>Toxic or infectious materials</li>
                <li>Radioactive materials</li>
                <li>Lithium batteries (loose/unpackaged)</li>
              </ul>
            </div>

            <p className="text-xs text-muted-foreground">
              This policy ensures compliance with the Occupational Health and Safety Act (Act No. 93 of 1996) 
              and protects our personnel, customers, and property.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setShowDangerousGoodsDialog(false)}>
              I Understand
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParcelDescriptionField;